import { useEffect, useRef } from "react";

const SECTION_IDS = [
    "top",
    "problem",
    "solution",
    "product",
    "metrics",
    "dashboard",
    "pricing",
    "competitive",
    "traction",
    "team",
    "cta",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
    try {
        let sid = sessionStorage.getItem("davini.sid");
        if (!sid) {
            sid =
                (crypto?.randomUUID?.() ||
                    Math.random().toString(36).slice(2)) +
                "-" +
                Date.now().toString(36);
            sessionStorage.setItem("davini.sid", sid);
        }
        return sid;
    } catch (_) {
        return "anon-" + Date.now();
    }
}

/**
 * Hook: observe section visibility and accumulate dwell time per slide.
 * Flushes events to the backend on visibility change, beforeunload, or every 30s.
 */
export function useSlideAnalytics(lang = "en", { enabled = true } = {}) {
    const accumRef = useRef(new Map()); // slide_id -> ms accumulated since last flush
    const activeRef = useRef(null); // { id, startedAt }
    const sessionIdRef = useRef(getSessionId());
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    useEffect(() => {
        if (!enabled) return;

        // Track time spent on currently active slide
        const stopActive = (now = performance.now()) => {
            const a = activeRef.current;
            if (!a) return;
            const delta = now - a.startedAt;
            if (delta > 0) {
                const prev = accumRef.current.get(a.id) || 0;
                accumRef.current.set(a.id, prev + delta);
            }
            activeRef.current = null;
        };

        const setActive = (id) => {
            const now = performance.now();
            stopActive(now);
            activeRef.current = { id, startedAt: now };
        };

        // IntersectionObserver — pick the section whose largest area is in viewport
        const observed = SECTION_IDS.map((id) => document.getElementById(id)).filter(
            Boolean
        );
        const visibilityMap = new Map(); // id -> intersectionRatio

        const pickWinner = () => {
            let best = null;
            let bestRatio = 0;
            for (const [id, ratio] of visibilityMap.entries()) {
                if (ratio > bestRatio) {
                    bestRatio = ratio;
                    best = id;
                }
            }
            if (best && bestRatio > 0.2) {
                if (!activeRef.current || activeRef.current.id !== best) setActive(best);
            } else {
                stopActive();
            }
        };

        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    visibilityMap.set(e.target.id, e.intersectionRatio);
                }
                pickWinner();
            },
            { threshold: [0, 0.25, 0.5, 0.75, 1] }
        );
        observed.forEach((el) => io.observe(el));

        const flush = (useBeacon = false) => {
            stopActive();
            const events = [];
            for (const [slide_id, dwell_ms] of accumRef.current.entries()) {
                if (dwell_ms >= 200) {
                    events.push({
                        session_id: sessionIdRef.current,
                        slide_id,
                        dwell_ms: Math.round(dwell_ms),
                        lang,
                        referrer: document.referrer || null,
                        user_agent: navigator.userAgent.slice(0, 256),
                    });
                }
            }
            accumRef.current.clear();
            if (events.length === 0) return;
            const url = `${API}/analytics/event`;
            const payload = JSON.stringify({ events });
            try {
                if (useBeacon && navigator.sendBeacon) {
                    const blob = new Blob([payload], { type: "application/json" });
                    navigator.sendBeacon(url, blob);
                } else {
                    fetch(url, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: payload,
                        keepalive: true,
                    }).catch(() => {});
                }
            } catch (_) {
                /* swallow */
            }
            // Restart active timer if a slide is still in view
            pickWinner();
        };

        const interval = setInterval(() => flush(false), 30000);
        const onVisChange = () => {
            if (document.visibilityState === "hidden") flush(true);
            else pickWinner();
        };
        const onUnload = () => flush(true);

        document.addEventListener("visibilitychange", onVisChange);
        window.addEventListener("beforeunload", onUnload);
        window.addEventListener("pagehide", onUnload);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisChange);
            window.removeEventListener("beforeunload", onUnload);
            window.removeEventListener("pagehide", onUnload);
            io.disconnect();
            flush(true);
        };
    }, [enabled, lang]);
}
